import { ChangeDetectionStrategy, Component, computed, effect, inject, input, output, signal } from '@angular/core';
import { tenantLogoUrl } from '@/cloud/models/tenant.model';
import { TranslationService } from '@/layout/service/translation.service';
import { CloudApiService } from '@/cloud/services/cloud-api.service';

@Component({
    selector: 'app-tenant-logo',
    standalone: true,
    template: `
        @if (source(); as src) {
            <img [src]="src" alt="" referrerpolicy="no-referrer" (error)="onError(src)" />
        } @else if (fallback()) {
            <i class="fa fa-building" aria-hidden="true"></i>
        }
    `,
    styles: `
        :host {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            flex: 0 0 auto;
            width: var(--tenant-logo-width, var(--tenant-logo-size, 2.25rem));
            height: var(--tenant-logo-height, var(--tenant-logo-size, 2.25rem));
            overflow: hidden;
            color: var(--primary-color);
        }
        :host(.tenant-logo--empty) {
            display: none;
        }
        /* Default: fit inside the square box. Callers can switch to natural sizing via the
           --tenant-logo-image-* properties (e.g. the topbar caps only the height). */
        img {
            width: var(--tenant-logo-image-width, 100%);
            height: var(--tenant-logo-image-height, 100%);
            max-width: var(--tenant-logo-image-max-width, none);
            max-height: var(--tenant-logo-image-max-height, none);
            object-fit: contain;
        }
        i {
            font-size: 1.2rem;
        }
    `,
    host: { 'aria-hidden': 'true', '[attr.title]': 'failed() ? t()("tenant.logoUnavailable") : null', '[class.tenant-logo--empty]': '!source() && !fallback()' },
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class TenantLogoComponent {
    readonly logoUrl = input<string | null>(null);
    readonly tenantId = input<string | null>(null);
    readonly blobId = input<string | null>(null);
    /** Show a building icon when there is no (loadable) logo; when false the component collapses. */
    readonly fallback = input(true);
    readonly loadError = output<void>();
    readonly t = inject(TranslationService).t;
    private failedSource = signal<string | null>(null);
    private readonly api = inject(CloudApiService);
    private readonly blobUrl = signal<string | null>(null);
    private readonly blobFailed = signal(false);
    readonly failed = computed(() => (this.blobId() ? this.blobFailed() : !!this.logoUrl() && (!tenantLogoUrl(this.logoUrl()) || tenantLogoUrl(this.logoUrl()) === this.failedSource())));
    readonly source = computed(() => (this.failed() ? null : this.blobId() ? this.blobUrl() : tenantLogoUrl(this.logoUrl())));

    private loadBlob = effect((onCleanup) => {
        const id = this.tenantId();
        const version = this.blobId();
        this.blobUrl.set(null);
        this.blobFailed.set(false);
        if (!id || !version) return;
        let url: string | null = null;
        const subscription = this.api.getNamespaceLogo(id, version).subscribe({
            next: (blob) => {
                if (blob.type !== 'image/png' || !blob.size || blob.size > 512 * 1024) {
                    this.blobFailed.set(true);
                    this.loadError.emit();
                    return;
                }
                url = URL.createObjectURL(blob);
                this.blobUrl.set(url);
            },
            error: () => {
                this.blobFailed.set(true);
                this.loadError.emit();
            }
        });
        onCleanup(() => {
            subscription.unsubscribe();
            if (url) URL.revokeObjectURL(url);
        });
    });

    onError(source: string) {
        if (source !== this.source()) return;
        if (this.blobId()) this.blobFailed.set(true);
        this.failedSource.set(source);
        this.loadError.emit();
    }
}
